import {Component, OnInit, ViewChild} from '@angular/core';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {BoxesRoutesEnum} from '../services/navigation/boxes-routes.enum';
import {ApiBoxResponse} from '../services/http/api-box-response';
import {FormBuilder, FormControl} from '@angular/forms';
import {manufactureDateRangeValidator} from '../../sensor/services/validator/manufacture-date-range.validator';
import {BoxRestApiService} from '../services/http/box-rest-api.service';
import {MatSort, SortDirection} from '@angular/material/sort';
import {
  catchError,
  debounce,
  Observable,
  Subject,
  switchMap,
  takeUntil,
  timer,
  of,
  filter,
  tap,
  map,
} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiBoxesListResponse} from '../services/http/api-boxes-list-response';
import {ApiBoxesListPaginatedQueryParams} from '../services/http/api-boxes-list-paginated-query-params';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {Utils} from '@astek/utils';
import {PageEvent} from '@angular/material/paginator';
import {MatDialog} from '@angular/material/dialog';
import {ApiRefreshTimerEnum} from '../../../core/services/http/api-refresh-timer.enum';
import {createdDateRangeValidator} from '../services/validator/created-date-range.validator';
import {BoxStateEnum} from '../enum/box-state.enum';
import {LocationResponse} from '../../../shared/location/location-response';
import {SelectionModel} from '@angular/cdk/collections';
import {HealthCenterRestApiService} from '../../health-center/services/http/health-center-rest-api.service';
import {HealthCenterShort} from '../../health-center/services/http/health-center-short';
import {ApiHealthCentersShortListItemResponse}
  from '../../health-center/services/http/api-health-centers-short-list-item-response';
import {ApiBatchUpdateBoxHealthCenterBoxRequest} from '../services/http/api-batch-update-box-health-center';
import {HealthCenterSelectDialogComponent} from '../health-center-select-dialog/health-center-select-dialog.component';
import {HealthCenterSelectDialogData} from '../health-center-select-dialog/health-center-select-dialog-data';
import {ConfirmDialogData} from '../../../core/components/confirm-dialog/confirm-dialog-data';
import {ConfirmDialogComponent} from '../../../core/components/confirm-dialog/confirm-dialog.component';
import {ApiSendBundleRequest} from '../services/http/api-send-bundle-request';
import {BoxStatusEnum} from '../enum/box-status.enum';
import {UserRolesEnum} from '../../../core/services/user/user-roles.enum';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';

@Component({
  selector: 'xp-boxes-list',
  templateUrl: './boxes-list.component.html',
  styleUrls: ['../../crud.common.scss', './boxes-list.component.scss'],
})
export class BoxesListComponent implements OnInit {

  public isLoading: boolean = true;

  public boxes: Array<ApiBoxResponse> | null = null;
  public selection = new SelectionModel<ApiBoxResponse>(true, []);
  public healthCenters: Array<HealthCenterShort> = [];

  public itemsCount: number = 0;
  public pageIndex: number = 0;
  public pageSize: number = 10;

  public displayedColumns: Array<string> = [
    'selection',
    'serialNumber',
    'createdAt',
    'manufacturedAt',
    'activatedAt',
    'usedAt',
    'status',
    'state',
    'firmwareVersion',
    'healthCenter',
    'location',
  ];

  public readonly formGroup = this._formBuilder.group({
    search: new FormControl<string>(''),
    manufacturedAtFrom: new FormControl<Date | null>(null),
    manufacturedAtTo: new FormControl<Date | null>(null),
    createdAtFrom: new FormControl<Date | null>(null),
    createdAtTo: new FormControl<Date | null>(null),
    state: new FormControl<string | null>(null),
  }, {validators: [manufactureDateRangeValidator, createdDateRangeValidator]});

  public defaultSortOn: string = 'serialNumber';
  public defaultSortOrder: SortDirection = 'asc';
  @ViewChild(MatSort)
  public sort: MatSort = new MatSort();

  private _requestApiOnChangeSubject: Subject<number> = new Subject<number>();

  constructor(
    private readonly _authenticationService: AuthenticationService,
    private readonly _boxRestApiService: BoxRestApiService,
    private readonly _healthCenterRestApiService: HealthCenterRestApiService,
    private readonly _formBuilder: FormBuilder,
    private readonly _snackBar: MatSnackBar,
    private readonly _translocoService: TranslocoService,
    private readonly _dialog: MatDialog,
  ) {
    this._requestApiOnChangeSubject.pipe(
      takeUntilDestroyed(),
      debounce((dueTime: number) => timer(dueTime)),
      tap(() => this.selection.clear()),
      switchMap(() => this.requestBoxes().pipe(
        takeUntil(this._requestApiOnChangeSubject),
        catchError(() => {
          this.handleError();
          return of(null);
        }),
      )),
    ).subscribe({
      next: (response: ApiBoxesListResponse | null) => {
        this.selection.clear();
        if (Utils.isNotNullOrUndefined(response)) {
          this.handleRequestBoxesResponse(response);
        }
      },
    });
  }

  public get batchCreateBoxRoute(): string {
    return '/' + RoutesEnum.BOXES + '/' + BoxesRoutesEnum.BATCH_CREATE;
  }

  public get searchValue(): string {
    return this.formGroup.controls.search.value?.trim() ?? '';
  }

  public get isAdmin(): boolean {
    return this._authenticationService.user?.roles.includes(UserRolesEnum.ADMIN) ?? false;
  }

  public get states(): Array<{ key: string, value: BoxStateEnum }> {
    return Object.entries(BoxStateEnum).map(([key, value]) => {
      return {
        key: key,
        value: value,
      };
    });
  }

  public ngOnInit(): void {
    this.initHealthCenters();
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public onSearchInput(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.KEYBOARD_INPUT);
  }

  public clearSearch(): void {
    this.pageIndex = 0;
    this.formGroup.controls.search.setValue('');
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public onDateChange(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onSelectionChange(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public resetFilters(): void {
    this.formGroup.controls.search.setValue('');
    this.formGroup.controls.manufacturedAtFrom.setValue(null);
    this.formGroup.controls.manufacturedAtTo.setValue(null);
    this.formGroup.controls.createdAtFrom.setValue(null);
    this.formGroup.controls.createdAtTo.setValue(null);
    this.formGroup.controls.state.setValue(null);
    this.clearSearch();
    this.formGroup.markAsPristine();
  }

  public onSortChange(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onPaginatorChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public openBatchUpdateHealthCenterModal(): void {
    const dialogData: HealthCenterSelectDialogData = {
      healthCenters: this.healthCenters,
    };
    const dialogRef = this._dialog.open(
      HealthCenterSelectDialogComponent, {data: dialogData},
    );
    dialogRef.afterClosed().pipe(
      filter(result => !!result),
      map((result: HealthCenterShort) => {
        const data: ApiBatchUpdateBoxHealthCenterBoxRequest = {
          healthCenterId: result.id,
          boxIds: this.selection.selected.map((box) => box.id),
        };
        return data;
      }),
      switchMap(data => this.requestBatchUpdateHeathCenter(data)),
    ).subscribe({
      next: () => this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY),
      error: () => this.handleError(),
    });
  }

  public confirmSendBundle(): void {
    const dialogData: ConfirmDialogData = {
      confirmTextKey: 'BOXES.CONFIRM_DIALOG_SEND_BUNDLE.CONFIRM',
      cancelTextKey: 'BOXES.CONFIRM_DIALOG_SEND_BUNDLE.CANCEL',
      message: 'BOXES.CONFIRM_DIALOG_SEND_BUNDLE.MESSAGE',
      title: 'BOXES.CONFIRM_DIALOG_SEND_BUNDLE.TITLE',
    };
    const dialogRef = this._dialog.open(
      ConfirmDialogComponent, {data: dialogData},
    );
    dialogRef.afterClosed().pipe(
      filter(confirm => confirm === true),
      switchMap(() => this.requestSendBundle()),
    ).subscribe({
      next: () => {
        this._snackBar.open(
          this._translocoService.translate('BOXES.CONFIRM_DIALOG_SEND_BUNDLE.SUCCESS'),
          '',
          {duration: 7000},
        );
        this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
      },
      error: () => this.handleError(),
    });
  }

  public getLocation(location: LocationResponse | null): string {
    return location ? location.latitude + ', ' + location.longitude : '';
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.boxes ? this.boxes.length : 0;
    return numSelected === numRows;
  }

  public toggleAll(): void {
    if (Utils.isNull(this.boxes) || this.boxes.length < 1) {
      return;
    }

    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.boxes.forEach(row => this.selection.select(row));
  }

  public isDisabledBatchUpdateHealthCenter(): boolean {
    return this.isLoading || this.healthCenters.length === 0 || this.selection.isEmpty();
  }

  private initHealthCenters(): void {
    this._healthCenterRestApiService.requestHealthCenterShortListOfName().pipe(
      map((healthCenters: Array<ApiHealthCentersShortListItemResponse>) => healthCenters.map(
        (message: ApiHealthCentersShortListItemResponse) => (new HealthCenterShort()).fromMessage(message)),
      ),
      tap((response: Array<HealthCenterShort>) => {
        this.healthCenters = response;
      }),
    ).subscribe();
  }

  private requestBoxes(): Observable<ApiBoxesListResponse> {
    this.isLoading = true;
    const queryParams: ApiBoxesListPaginatedQueryParams = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sort: this.sort?.active ?? this.defaultSortOn,
      order: this.sort?.direction ?? this.defaultSortOrder,
      search: this.searchValue,
      manufacturedAtFrom: this.formGroup.controls.manufacturedAtFrom.value,
      manufacturedAtTo: this.formGroup.controls.manufacturedAtTo.value,
      createdAtFrom: this.formGroup.controls.createdAtFrom.value,
      createdAtTo: this.formGroup.controls.createdAtTo.value,
      state: Utils.isNull(this.formGroup.controls.state.value)
        ? null
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        : (this.formGroup.controls.state.value === BoxStateEnum.OK ? 1 : 0),
    };
    return this._boxRestApiService.requestBoxListPaginated(queryParams);
  }

  private handleRequestBoxesResponse(response: ApiBoxesListResponse): void {
    this.isLoading = false;
    this.itemsCount = response.totalCount;
    this.boxes = response.boxes;
  }

  private requestSendBundle(): Observable<void> {
    const data: ApiSendBundleRequest = {
      boxIds: this.selection.selected
        .filter((box) => box.status === BoxStatusEnum.CREATED.toString())
        .map((box) => box.id),
    };

    return this._boxRestApiService.requestSendBundle(data);
  }

  private requestBatchUpdateHeathCenter(request: ApiBatchUpdateBoxHealthCenterBoxRequest): Observable<void> {
    this.isLoading = true;
    return this._boxRestApiService.requestBatchUpdateHeathCenter(request);
  }

  private handleError(): void {
    this.isLoading = false;
    this._snackBar.open(
      this._translocoService.translate('SHARED.ERRORS.UNKNOWN'),
      this._translocoService.translate('SHARED.CLOSE'),
    );
  }
}
